import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { IExtensaoArquivo, ILevantamentoQuantitativo } from "../../../models"
import { IFormError, IFormProps } from "../../utils"

interface ICreateLevantamentoQuantitativoFormFields {
    Nome: string
    Colunas: string[]
}

interface IEditLevantamentoQuantitativoFormFields {
    IdVelesGeoLevantamentoQuantitativo: number
    Nome: string
    Colunas: string[]
}

interface ILevantamentoQuantitativoState {
    levantamentosDoProjeto: ILevantamentoQuantitativo[]
    createLevantamentoForm: IFormProps<ICreateLevantamentoQuantitativoFormFields>
    createLevantamentoFormOpen: boolean
    editLevantamentoForm: IFormProps<IEditLevantamentoQuantitativoFormFields>
    editLevantamentoFormOpen: boolean
    openDownloadLevantamento: boolean
    extensaoArquivoSelecionada?: IExtensaoArquivo
    levantamentoSelecionado?: ILevantamentoQuantitativo
    openDeleteLevantamento: boolean
}

const createLevantamentoFormInitialState: ICreateLevantamentoQuantitativoFormFields = {
    Nome: "",
    Colunas: []
}

const editLevantamentoFormInitialState: IEditLevantamentoQuantitativoFormFields = {
    IdVelesGeoLevantamentoQuantitativo: 0,
    Nome: "",
    Colunas: []
}

const initialState: ILevantamentoQuantitativoState = {
    levantamentosDoProjeto: [],
    createLevantamentoForm: {
        errors: [],
        fields: createLevantamentoFormInitialState,
        touched: false
    },
    createLevantamentoFormOpen: false,
    editLevantamentoForm: {
        errors: [],
        fields: editLevantamentoFormInitialState,
        touched: false
    },
    editLevantamentoFormOpen: false,
    openDownloadLevantamento: false,
    openDeleteLevantamento: false
}

export const validateCreateLevantamentoQuantitativoForm = (fields: ICreateLevantamentoQuantitativoFormFields): IFormError<keyof ICreateLevantamentoQuantitativoFormFields>[] => {
    const errors: IFormError<keyof ICreateLevantamentoQuantitativoFormFields>[] = [];

    if (!fields.Nome) {
        errors.push({
            key: 'Nome',
            message: 'O nome não pode ficar em branco',
        });
    } else if (fields.Nome.length === 0 || fields.Nome.replace('  ', ' ').trim().length === 0) {
        errors.push({
            key: 'Nome',
            message: 'O nome não pode ficar em branco'
        })
    } else if (fields.Colunas.length === 0) {
        errors.push({
            key: 'Colunas',
            message: 'Selecione ao menos uma coluna'
        })
    }

    return errors;
}

export const validateEditLevantamentoQuantitativoForm = (fields: IEditLevantamentoQuantitativoFormFields): IFormError<keyof IEditLevantamentoQuantitativoFormFields>[] => {
    const errors: IFormError<keyof IEditLevantamentoQuantitativoFormFields>[] = [];

    if (!fields.Nome) {
        errors.push({
            key: 'Nome',
            message: 'Nome é obrigatório',
        });
    } else if (fields.Nome.length === 0 || fields.Nome.replace('  ', ' ').trim().length === 0) {
        errors.push({
            key: 'Nome',
            message: 'O nome do layout não pode ficar em branco'
        })
    } else if (fields.Colunas.length === 0) {
        errors.push({
            key: 'Colunas',
            message: 'Selecione ao menos uma coluna'
        })
    }

    return errors;
}

const levantamentoQuantitativoSlice = createSlice({
    name: 'levantamentoQuantitativo',
    initialState,
    reducers: {
        setLevantamentosDoProjetoState(state, action: PayloadAction<ILevantamentoQuantitativo[]>) {
            state.levantamentosDoProjeto = action.payload
        },
        resetLevantamentosDoProjetoState(state) {
            state.levantamentosDoProjeto = initialState.levantamentosDoProjeto
        },
        setExtensaoArquivoSelecionadoState(state, action: PayloadAction<IExtensaoArquivo>) {
            state.extensaoArquivoSelecionada = action.payload
        },
        resetExtensaoArquivoSelecionadoState(state) {
            state.extensaoArquivoSelecionada = initialState.extensaoArquivoSelecionada
        },
        setCreateLevantamentoFormNomeState(state, action: PayloadAction<string>) {
            state.createLevantamentoForm.fields.Nome = action.payload
        },
        setCreateLevantamentoFormColunasState(state, action: PayloadAction<string[]>) {
            state.createLevantamentoForm.fields.Colunas = action.payload
        },
        setCreateLevantamentoFormErrorsState(state, action: PayloadAction<IFormError<keyof ICreateLevantamentoQuantitativoFormFields>[]>) {
            state.createLevantamentoForm.errors = action.payload
        },
        setCreateLevantamentoFormTouchedState(state, action: PayloadAction<boolean>) {
            state.createLevantamentoForm.touched = action.payload
        },
        resetCreateLevantamentoFormState(state) {
            state.createLevantamentoForm = initialState.createLevantamentoForm
        },
        setCreateLevantamentoFormOpenState(state, action: PayloadAction<boolean>) {
            state.createLevantamentoFormOpen = action.payload
        },
        setEditLevantamentoFormIdVelesGeoLevantamentoQuantitativoState(state, action: PayloadAction<number>) {
            state.editLevantamentoForm.fields.IdVelesGeoLevantamentoQuantitativo = action.payload
        },
        setEditLevantamentoFormNomeState(state, action: PayloadAction<string>) {
            state.editLevantamentoForm.fields.Nome = action.payload
        },
        setEditLevantamentoFormColunasState(state, action: PayloadAction<string[]>) {
            state.editLevantamentoForm.fields.Colunas = action.payload
        },
        setEditLevantamentoFormErrorsState(state, action: PayloadAction<IFormError<keyof IEditLevantamentoQuantitativoFormFields>[]>) {
            state.editLevantamentoForm.errors = action.payload
        },
        setEditLevantamentoFormTouchedState(state, action: PayloadAction<boolean>) {
            state.editLevantamentoForm.touched = action.payload
        },
        resetEditLevantamentoFormState(state) {
            state.editLevantamentoForm = initialState.editLevantamentoForm
        },
        setEditLevantamentoFormOpenState(state, action: PayloadAction<boolean>) {
            state.editLevantamentoFormOpen = action.payload
        },
        setOpenDownloadLevantamentoState(state, action: PayloadAction<boolean>) {
            state.openDownloadLevantamento = action.payload
        },
        setLevantamentoSelecionadoState(state, action: PayloadAction<ILevantamentoQuantitativo>) {
            state.levantamentoSelecionado = action.payload
        },
        resetLevantamentoSelecionadoState(state) {
            state.levantamentoSelecionado = initialState.levantamentoSelecionado
        },
        setOpenDeleteLevantamentoState(state, action: PayloadAction<boolean>) {
            state.openDeleteLevantamento = action.payload
        }
    }
})

export const {
    resetCreateLevantamentoFormState,
    resetEditLevantamentoFormState,
    resetExtensaoArquivoSelecionadoState,
    resetLevantamentosDoProjetoState,
    setCreateLevantamentoFormColunasState,
    setCreateLevantamentoFormErrorsState,
    setCreateLevantamentoFormNomeState,
    setCreateLevantamentoFormOpenState,
    setCreateLevantamentoFormTouchedState,
    setEditLevantamentoFormColunasState,
    setEditLevantamentoFormErrorsState,
    setEditLevantamentoFormIdVelesGeoLevantamentoQuantitativoState,
    setEditLevantamentoFormNomeState,
    setEditLevantamentoFormOpenState,
    setEditLevantamentoFormTouchedState,
    setExtensaoArquivoSelecionadoState,
    setLevantamentosDoProjetoState,
    setOpenDownloadLevantamentoState,
    resetLevantamentoSelecionadoState,
    setLevantamentoSelecionadoState,
    setOpenDeleteLevantamentoState
} = levantamentoQuantitativoSlice.actions

export const levantamentoQuantitativoReducer = levantamentoQuantitativoSlice.reducer